import { useMemo, useState } from 'react'
import { Tooltip } from '@mantine/core'
import { Line } from 'react-chartjs-2'
import 'chart.js/auto'
import dayjs from '@/lib/dayjs'

/**
 * The four B2C availability states, in stacking order (bottom to top).
 *
 * The healthy state is deliberately quiet: it is the bulk of the catalogue
 * and must recede. Both preorders share one hue (a qualification of the same
 * state): amber, then a deeper orange with a fine texture on top — never told
 * apart by hue alone. Unavailable is the only cold strong colour on the page.
 */
export const STATES = [
    { key: 'available', label: 'En stock', color: '#dde3ea', ink: '#6b7280', textured: false },
    { key: 'preorder', label: 'Précommande', color: '#f5b942', ink: '#b7791f', textured: false },
    { key: 'overdue', label: 'Précommande dépassée', color: '#e07a1f', ink: '#b45309', textured: true },
    { key: 'unavailable', label: 'Non commandable', color: '#e5484d', ink: '#c4383c', textured: false },
]

/** Cells before a product enters the scope: no fill, just the page ground. */
export const OUT_OF_SCOPE = 'transparent'

/** CSS background for a state: flat, or thin light diagonals over it. */
export const stateBackground = (state) =>
    state.textured
        ? `repeating-linear-gradient(-45deg, ${state.color} 0 3px, rgba(255,255,255,0.45) 3px 4px)`
        : state.color

/** The same diagonals as a canvas pattern for Chart.js. */
const texturePattern = () => {
    if (typeof document === 'undefined') return STATES[2].color
    const size = 6
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = STATES[2].color
    ctx.fillRect(0, 0, size, size)
    ctx.strokeStyle = 'rgba(255,255,255,0.45)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(size, size)
    ctx.moveTo(-size / 2, size / 2)
    ctx.lineTo(size / 2, size * 1.5)
    ctx.moveTo(size / 2, -size / 2)
    ctx.lineTo(size * 1.5, size / 2)
    ctx.stroke()
    return ctx.createPattern(canvas, 'repeat')
}

export const StateSwatch = ({ state, className = 'h-2.5 w-2.5' }) => (
    <span
        className={`inline-block shrink-0 rounded-[3px] ${className}`}
        style={{ background: stateBackground(state) }}
        aria-hidden="true"
    />
)

export const StatesLegend = ({ states = STATES, className = '' }) => (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 ${className}`}>
        {states.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
                <StateSwatch state={s} />
                {s.label}
            </span>
        ))}
    </div>
)

const fmtDay = (d) => dayjs(d).format('ddd D MMM')

/* -------------------------------------------------------------- today bar */

/**
 * Today's split for one product: a thin segmented bar, then the counts of
 * the states that are present. Reads at a glance, no pill per state.
 */
export const TodayBar = ({ today }) => {
    const total = today.reduce((a, b) => a + b, 0)
    if (!total) return <span className="text-xs text-gray-300">hors périmètre</span>
    return (
        <div className="min-w-0">
            <div className="flex h-1.5 gap-px overflow-hidden rounded-full">
                {STATES.map((s, i) =>
                    today[i] > 0 ? (
                        <span key={s.key} style={{ flex: today[i], background: stateBackground(s) }} />
                    ) : null
                )}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-3 text-xs tabular-nums">
                {STATES.map((s, i) =>
                    today[i] > 0 ? (
                        <span key={s.key} style={{ color: s.ink }} title={s.label}>
                            {today[i]}
                        </span>
                    ) : null
                )}
            </div>
        </div>
    )
}

/* ------------------------------------------------------------------ ribbon */

const dayDetail = (counts) =>
    STATES.map((s, i) => (counts[i] ? `${counts[i]} ${s.label.toLowerCase()}` : null))
        .filter(Boolean)
        .join(' · ')

/**
 * One column per day, split into up to four segments proportional to the
 * number of variants in each state. An empty column means the product was
 * not yet in scope that day — not that it was unavailable.
 */
export const Ribbon = ({ days, daily, height = 36 }) => {
    const [hover, setHover] = useState(null)

    const label =
        hover == null
            ? ''
            : daily[hover]
              ? `${fmtDay(days[hover])} · ${dayDetail(daily[hover])}`
              : `${fmtDay(days[hover])} · hors périmètre`

    return (
        <Tooltip.Floating label={label} disabled={hover == null} position="top" offset={10}>
            <div
                className="grid w-full gap-[2px]"
                style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`, height }}
                onMouseLeave={() => setHover(null)}
                role="img"
                aria-label="Historique jour par jour des états de disponibilité"
            >
                {daily.map((counts, i) => {
                    const total = counts ? counts.reduce((a, b) => a + b, 0) : 0
                    return (
                        <div
                            key={days[i]}
                            className="flex flex-col-reverse overflow-hidden rounded-[2px]"
                            style={{ background: total ? 'transparent' : OUT_OF_SCOPE }}
                            onMouseEnter={() => setHover(i)}
                        >
                            {total > 0 &&
                                STATES.map((s, k) =>
                                    counts[k] > 0 ? (
                                        <span key={s.key} style={{ flex: counts[k], background: stateBackground(s) }} />
                                    ) : null
                                )}
                        </div>
                    )
                })}
            </div>
        </Tooltip.Floating>
    )
}

/* ------------------------------------------------------------------ charts */

const chartScales = (yTicks = {}, yMax) => ({
    x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#9ca3af', font: { size: 11 }, maxRotation: 0, autoSkipPadding: 24 },
    },
    y: {
        stacked: true,
        beginAtZero: true,
        max: yMax,
        grid: { color: '#f3f4f6' },
        border: { display: false },
        ticks: { color: '#9ca3af', font: { size: 11 }, maxTicksLimit: 4, padding: 6, ...yTicks },
    },
})

const areaDataset = (state, data, background, index) => ({
    label: state.label,
    data,
    fill: index === 0 ? 'origin' : '-1',
    backgroundColor: background,
    borderColor: 'rgba(255,255,255,0.9)',
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 4,
    pointHoverBackgroundColor: state.color,
    pointHoverBorderColor: '#fff',
    pointHoverBorderWidth: 2,
    tension: 0.25,
})

/**
 * Stacked area over the window. `series` is one array per state, in STATES
 * order (only the states in `keys` are drawn). `totals` per day feed the
 * tooltip footer.
 */
const StackedArea = ({ days, series, totals, keys, normalized, yMax, height }) => {
    const pattern = useMemo(texturePattern, [])

    const drawn = STATES.map((s, i) => ({ state: s, values: series[i], index: i })).filter((d) =>
        keys.includes(d.state.key)
    )

    return (
        <div style={{ height }}>
            <Line
                data={{
                    labels: days.map((d) => dayjs(d).format('D MMM')),
                    datasets: drawn.map((d, k) =>
                        areaDataset(
                            d.state,
                            normalized ? d.values.map((v, j) => (totals[j] ? (100 * v) / totals[j] : null)) : d.values,
                            d.state.textured ? pattern : d.state.color,
                            k
                        )
                    ),
                }}
                options={{
                    maintainAspectRatio: false,
                    animation: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: '#111827',
                            padding: 10,
                            titleFont: { size: 12 },
                            bodyFont: { size: 12 },
                            footerFont: { size: 11, weight: 'normal' },
                            footerColor: '#9ca3af',
                            callbacks: {
                                title: (items) => fmtDay(days[items[0].dataIndex]),
                                label: (ctx) => {
                                    const j = ctx.dataIndex
                                    const raw = series[STATES.findIndex((s) => s.label === ctx.dataset.label)][j]
                                    const pct = totals[j] ? Math.round((100 * raw) / totals[j]) : 0
                                    return ` ${ctx.dataset.label} : ${raw} (${pct} %)`
                                },
                                footer: (items) => `${totals[items[0].dataIndex]} variantes suivies`,
                            },
                        },
                    },
                    scales: chartScales(normalized ? { callback: (v) => `${v} %` } : {}, yMax),
                }}
            />
        </div>
    )
}

/** Catalogue composition, normalised to 100 %: structure and its breaks. */
export const CompositionChart = ({ days, series, totals }) => (
    <StackedArea
        days={days}
        series={series}
        totals={totals}
        keys={STATES.map((s) => s.key)}
        normalized
        yMax={100}
        height={320}
    />
)

/** The two states to act on, in absolute counts: this is where values are read. */
export const IssuesChart = ({ days, series, totals }) => (
    <StackedArea
        days={days}
        series={series}
        totals={totals}
        keys={['overdue', 'unavailable']}
        normalized={false}
        height={240}
    />
)

/** The raw day-by-day table behind both charts. Colour never carries data alone. */
export const DailyTable = ({ days, series, totals }) => (
    <div className="max-h-96 overflow-auto rounded-lg border border-gray-200/70 bg-white">
        <table className="w-full text-xs tabular-nums">
            <thead className="sticky top-0 bg-white text-left text-gray-400">
                <tr>
                    <th className="px-4 py-2 font-medium">Jour</th>
                    {STATES.map((s) => (
                        <th key={s.key} className="px-4 py-2 text-right font-medium">
                            {s.label}
                        </th>
                    ))}
                    <th className="px-4 py-2 text-right font-medium">Suivies</th>
                    <th className="px-4 py-2 text-right font-medium">En stock</th>
                </tr>
            </thead>
            <tbody>
                {days.map((d, j) => (
                    <tr key={d} className="border-t border-gray-100 text-gray-700">
                        <td className="px-4 py-1.5">{fmtDay(d)}</td>
                        {STATES.map((s, i) => (
                            <td key={s.key} className="px-4 py-1.5 text-right">
                                {series[i][j]}
                            </td>
                        ))}
                        <td className="px-4 py-1.5 text-right text-gray-400">{totals[j]}</td>
                        <td className="px-4 py-1.5 text-right text-gray-400">
                            {totals[j] ? `${Math.round((100 * series[0][j]) / totals[j])} %` : '—'}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)
